import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { db } from '../firebase';
import Footer from '../components/Footer';
import { formatCurrencyInput } from '../utils/currencyUtils';
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc
} from 'firebase/firestore';
// Filter pills shared with the Wantlist page (WantList.jsx) and the public
// pedalboard (PublicPedalboard.jsx): label inside a gold-bordered pill,
// borderless control filling the rest. Kept local the way both of those pages
// keep their copies. The old right-hand white card with a label/select grid
// is gone (owner, 2026-11-02: the public want list was the last page still
// wearing the old filter style after the 1.1.3 pedalboard change).
const WANT_FILTER_FIELD_STYLE = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.65rem',
  width: '100%',
  borderRadius: '25px',
  border: '1px solid #C89F3C',
  backgroundColor: 'white',
  padding: '0.65rem 1rem 0.65rem 1.1rem',
  boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.1)',
  boxSizing: 'border-box',
};
const WANT_FILTER_LABEL_STYLE = {
  fontSize: '0.9rem',
  fontWeight: 600,
  color: '#4A5759',
  fontFamily: 'Nunito, sans-serif',
  whiteSpace: 'nowrap',
  flexShrink: 0,
};
const WANT_FILTER_CONTROL_BASE = {
  fontSize: '0.9375rem',
  fontFamily: 'Nunito, sans-serif',
  fontWeight: 400,
  color: '#374151',
  width: '100%',
  flex: 1,
  minWidth: 0,
  border: 'none',
  outline: 'none',
  padding: '0.15rem 0',
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
};
const WANT_FILTER_SELECT_STYLE = {
  ...WANT_FILTER_CONTROL_BASE,
  cursor: 'pointer',
  appearance: 'none',
  WebkitAppearance: 'none',
  MozAppearance: 'none',
  paddingRight: '2rem',
  backgroundImage: "url(\"data:image/svg+xml;charset=UTF-8,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'%3E%3Cpath fill='%23C89F3C' d='M6 8L0 0h12z'/%3E%3C/svg%3E\")",
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 0.25rem center',
};
const WANT_FILTER_INPUT_STYLE = {
  ...WANT_FILTER_CONTROL_BASE,
  MozAppearance: 'textfield',
};

const EMPTY_WANT_FILTERS = { make: '', maxPrice: '', effectType: '' };


const PublicWantList = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [wants, setWants] = useState([]);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBackToTop, setShowBackToTop] = useState(false);
  const [filters, setFilters] = useState({ ...EMPTY_WANT_FILTERS });

  // Show/hide "Back to Top" button based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 300) {
        setShowBackToTop(true);
      } else {
        setShowBackToTop(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Function to format price input to max 2 decimal places
  // Apply filters to wants
  const applyFilters = (wantsList) => {
    return wantsList.filter((want) => {
      if (filters.make && want.make !== filters.make) return false;
      if (filters.maxPrice && want.maxPrice && Number(want.maxPrice) > Number(filters.maxPrice)) return false;
      if (filters.effectType && (!want.effectTypes || !want.effectTypes.includes(filters.effectType))) return false;
      return true;
    });
  };

  // Get unique values for filters
  const getAvailableMakes = () => {
    const makes = new Set();
    wants.forEach(want => {
      if (want.make) {
        makes.add(want.make);
      }
    });
    return Array.from(makes).sort();
  };

  const getAvailableEffectTypes = () => {
    const typeCounts = {};
    wants.forEach(want => {
      if (want.effectTypes && Array.isArray(want.effectTypes)) {
        want.effectTypes.forEach(type => {
          typeCounts[type] = (typeCounts[type] || 0) + 1;
        });
      }
    });
    return Object.entries(typeCounts)
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([type, count]) => ({ type, count }));
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        // PII isolation: read sanitized public mirror.
        const userRef = doc(db, 'publicProfiles', userId);
        const userSnap = await getDoc(userRef);
        
        if (userSnap.exists()) {
          const data = userSnap.data();
          
          // 🚫 CHECK IF USER IS BANNED - Don't show their Wantlist
          if (data.isBanned === true) {
            console.log('🚫 User is banned - not showing Wantlist');
            setUserData(null); // Treat as user not found
            setLoading(false);
            return;
          }
          
          setUserData({
            username: data.username || 'Unknown User',
            photoURL: data.photoURL || '/images/default-avatar.png'
          });
        } else {
          setUserData(null);
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        setUserData(null);
      }
    };

    const fetchWants = async () => {
      try {
        setLoading(true);
        const startTime = performance.now();
        console.log(`⏱️ Starting Wantlist fetch for user: ${userId}`);
        
        const wantsRef = collection(db, 'wantList');
        const q = query(wantsRef, where('uid', '==', userId));
        const querySnapshot = await getDocs(q);
        
        const fetchedWants = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data()
        }));

        setWants(fetchedWants);
        
        const endTime = performance.now();
        console.log(`✅ Wantlist fetch complete in ${(endTime - startTime).toFixed(2)}ms - Found ${fetchedWants.length} wants`);
      } catch (err) {
        console.error('Failed to load Wantlist:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
    fetchWants();
  }, [userId]);

  const filteredWants = applyFilters(wants);
  const availableMakes = getAvailableMakes();
  const availableEffectTypes = getAvailableEffectTypes();

  if (loading) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontFamily: 'Nunito, sans-serif'
      }}>
        <p style={{ fontSize: '1.125rem', color: '#6B7280' }}>Loading Wantlist...</p>
      </div>
    );
  }

  if (!userData) {
    return (
      <div style={{ 
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: 'Nunito, sans-serif',
        padding: '2rem'
      }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#374151', marginBottom: '1rem' }}>
          User not found
        </h1>
        <p style={{ color: '#6B7280', marginBottom: '1.5rem' }}>
          This user's Wantlist is not available.
        </p>
        <button
          onClick={() => navigate(-1)}
          style={{
            backgroundColor: '#5A9ECB',
            color: 'white',
            padding: '0.5rem 1rem',
            borderRadius: '9999px',
            border: 'none',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Go Back
        </button>
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      fontFamily: 'Nunito, sans-serif',
      backgroundColor: '#F7F3EB',
      overflowX: 'hidden',
      width: '100%'
    }}>
      <style>{`
        @media (max-width: 1024px) {
          .public-wantlist-container {
            padding: 0 0.5rem 2rem !important;
          }
          .wants-column {
            padding: 1rem !important;
          }
          .public-wantlist-header {
            padding: 1rem !important;
          }
          .public-wantlist-header h1 {
            font-size: 1.125rem !important;
          }
        }
      `}</style>
      {/* Header */}
      <div 
        className="public-wantlist-header"
        style={{
          backgroundColor: 'white',
          borderBottom: '1px solid #e5e7eb',
          padding: '1.5rem 2rem',
          marginBottom: '2rem',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        <div style={{ 
          maxWidth: '1200px', 
          margin: '0 auto', 
          display: 'flex', 
          alignItems: 'center', 
          gap: '1rem',
          width: '100%',
          boxSizing: 'border-box'
        }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'none',
              border: 'none',
              color: '#5A9ECB',
              cursor: 'pointer',
              fontSize: '1.5rem',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0
            }}
          >
            ←
          </button>
          <img
            src={userData.photoURL}
            alt={userData.username}
            style={{
              width: '48px',
              height: '48px',
              minWidth: '48px',
              minHeight: '48px',
              borderRadius: '50%',
              objectFit: 'cover',
              flexShrink: 0
            }}
          />
          <div style={{ minWidth: 0, overflow: 'hidden' }}>
            <h1 style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              color: '#1F2937',
              margin: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}>
              {userData.username}'s Wantlist
            </h1>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', margin: 0 }}>
              {filteredWants.length} {filteredWants.length === 1 ? 'want' : 'wants'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div
        className="public-wantlist-container"
        style={{
          maxWidth: '1200px',
          margin: '0 auto',
          padding: '0 1rem 2rem',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Filters: same pill fields as the Wantlist page and the public
            pedalboard; one grid above the wants instead of the old sticky
            right-hand card. */}
        <h2
          className="public-wantlist-filter-heading"
          style={{ margin: '1rem 0 0.5rem', color: '#4A5759', fontFamily: 'Nunito', fontSize: '1.25rem', fontWeight: '600' }}
        >
          Filter Wants
        </h2>
        <div
          className="public-want-list-filters"
          style={{ margin: '0 0 1.5rem', display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', columnGap: '1.25rem', rowGap: '1rem', boxSizing: 'border-box' }}
        >
          <style>{`
            @media (max-width: 768px) {
              .public-want-list-filters {
                grid-template-columns: 1fr !important;
                column-gap: 0 !important;
                row-gap: 0.75rem !important;
              }
            }
            .public-want-list-filters input[type="number"]::-webkit-outer-spin-button,
            .public-want-list-filters input[type="number"]::-webkit-inner-spin-button {
              -webkit-appearance: none;
              margin: 0;
            }
          `}</style>

          <div style={WANT_FILTER_FIELD_STYLE}>
            <span style={WANT_FILTER_LABEL_STYLE}>Brand</span>
            <select
              value={filters.make}
              onChange={(e) => { setFilters({ ...filters, make: e.target.value }); e.target.blur(); }}
              style={WANT_FILTER_SELECT_STYLE}
              aria-label="Filter by brand"
            >
              <option value=''></option>
              {availableMakes.map((make, idx) => (
                <option key={idx} value={make}>{make}</option>
              ))}
            </select>
          </div>


          <div style={WANT_FILTER_FIELD_STYLE}>
            <span style={WANT_FILTER_LABEL_STYLE}>Effect Type</span>
            <select
              value={filters.effectType}
              onChange={(e) => { setFilters({ ...filters, effectType: e.target.value }); e.target.blur(); }}
              style={WANT_FILTER_SELECT_STYLE}
              aria-label="Filter by effect type"
            >
              <option value=''></option>
              {availableEffectTypes.map((item, idx) => (
                <option key={idx} value={item.type}>{item.type} ({item.count})</option>
              ))}
            </select>
          </div>

          <div style={WANT_FILTER_FIELD_STYLE}>
            <span style={WANT_FILTER_LABEL_STYLE}>Max Price:</span>
            <input
              type='number'
              inputMode='numeric'
              value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: formatCurrencyInput(e.target.value) })}
              style={WANT_FILTER_INPUT_STYLE}
              aria-label="Maximum price"
            />
          </div>


        </div>

        {Object.values(filters).some(Boolean) && (
          <div style={{ margin: '-0.5rem 0 1.5rem' }}>
            <button
              onClick={() => setFilters({ ...EMPTY_WANT_FILTERS })}
              style={{
                padding: '0.5rem 1.25rem',
                backgroundColor: 'white',
                border: '1px solid #C89F3C',
                borderRadius: '25px',
                fontSize: '0.875rem',
                fontWeight: '600',
                fontFamily: 'Nunito, sans-serif',
                color: '#4A5759',
                cursor: 'pointer'
              }}
            >
              Clear Filters
            </button>
          </div>
        )}

        {/* Left Column - Wantlist */}
        <div 
          className="wants-column"
          style={{ 
            width: '100%',
            minWidth: '0',
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'hidden'
          }}
        >
          {filteredWants.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '3rem 1rem',
              color: '#6B7280'
            }}>
              <p style={{ fontSize: '1.125rem', marginBottom: '0.5rem' }}>
                {wants.length === 0 ? 'No wants yet' : 'No wants match your filters'}
              </p>
              <p style={{ fontSize: '0.875rem' }}>
                {wants.length === 0 ? 
                  `${userData.username} hasn't added any pedals to their Wantlist.` : 
                  'Try adjusting your filters to see more wants.'
                }
              </p>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '1.5rem' }}>
              {filteredWants.map((want) => (
                <div
                  key={want.id}
                  style={{
                    border: '1px solid #d1d5db',
                    borderRadius: '12px',
                    padding: '20px',
                    backgroundColor: '#f9fafb'
                  }}
                >
                  {/* Make/Model - Bold */}
                  {want.make && want.model && (
                    <>
                      <div style={{ 
                        fontSize: '1.125rem', 
                        fontWeight: 'bold',
                        color: '#1F2937',
                        marginBottom: '0.25rem'
                      }}>
                        {want.make}
                      </div>
                      <div style={{ 
                        fontSize: '1rem', 
                        fontWeight: 'bold',
                        color: '#4B5563',
                        marginBottom: '0.5rem'
                      }}>
                        {want.model}
                      </div>
                    </>
                  )}
                  
                  {/* Effect Types */}
                  {want.effectTypes && want.effectTypes.length > 0 && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      marginTop: want.make && want.model ? '8px' : '0',
                      marginBottom: '0.25rem'
                    }}>
                      <strong>Effect Type:</strong> {want.effectTypes.join(', ')}
                    </div>
                  )}
                  
                  {/* General Want fallback */}
                  {(!want.make || !want.model) && (!want.effectTypes || want.effectTypes.length === 0) && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      General Want
                    </div>
                  )}
                  
                  {/* Max Price */}
                  {want.maxPrice && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      <strong>Max Price:</strong> ${want.maxPrice}
                    </div>
                  )}
                  
                  {/* Condition */}
                  {want.condition && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      <strong>Minimum Condition Wanted:</strong> {want.condition}
                    </div>
                  )}
                  
                  {/* Decade */}
                  {want.decade && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      <strong>Decade:</strong> {want.decade}
                    </div>
                  )}
                  
                  {/* Color */}
                  {want.color && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#374151',
                      marginBottom: '0.25rem'
                    }}>
                      <strong>Color:</strong> {want.color}
                    </div>
                  )}
                  
                  {/* Description/Notes */}
                  {want.description && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#6B7280',
                      fontStyle: 'italic',
                      marginTop: '12px'
                    }}>
                      {want.description}
                    </div>
                  )}
                  
                  {want.notes && (
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: '#6B7280',
                      fontStyle: 'italic',
                      marginTop: '12px'
                    }}>
                      {want.notes}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>

      <Footer />

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="back-to-top-btn"
          style={{
            position: 'fixed',
            bottom: window.innerWidth <= 768 ? '90px' : '30px',
            right: '30px',
            backgroundColor: '#5A9ECB',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '36px',
            height: '36px',
            fontSize: '16px',
            fontWeight: 'bold',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
            zIndex: 1000,
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#4A8AB0';
            e.target.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#5A9ECB';
            e.target.style.transform = 'scale(1)';
          }}
          aria-label="Back to top"
        >
          ⌃
        </button>
      )}
    </div>
  );
};

export default PublicWantList;
